import { useEffect, useRef, useState } from 'react'

export interface UseMediaQueryOptions {
  getInitialValueInEffect: boolean
}

type MediaQueryCallback = (event: { matches: boolean; media: string }) => void

/**
 * Older versions of Safari (shipped withCatalina and before) do not support addEventListener on matchMedia
 * https://stackoverflow.com/questions/56466261/matchmedia-addlistener-marked-as-deprecated-addeventlistener-equivalent
 * */
const attachMediaListener = (query: MediaQueryList, callback: MediaQueryCallback): (() => void) => {
  try {
    query.addEventListener('change', callback)
    return () => query.removeEventListener('change', callback)
  } catch {
    query.addListener(callback)
    return () => query.removeListener(callback)
  }
}

const getInitialValue = (query: string, initialValue?: boolean): boolean => {
  if (initialValue === true || initialValue === false) {
    return initialValue
  }

  if (globalThis.window !== undefined && 'matchMedia' in globalThis) {
    return globalThis.matchMedia(query).matches
  }

  return false
}

// TODO: migrate to https://usehooks-ts.com/react-hook/use-media-query
export const useMediaQuery = (
  query: string,
  initialValue?: boolean,
  { getInitialValueInEffect }: UseMediaQueryOptions = {
    getInitialValueInEffect: true,
  },
): boolean | undefined => {
  const [matches, setMatches] = useState(
    getInitialValueInEffect ? initialValue : getInitialValue(query),
  )
  const queryRef = useRef<MediaQueryList | undefined>(undefined)

  useEffect(() => {
    if ('matchMedia' in globalThis) {
      queryRef.current = globalThis.matchMedia(query)
      setMatches(queryRef.current.matches)
      return attachMediaListener(queryRef.current, event => setMatches(event.matches))
    }

    return undefined
  }, [query])

  return matches
}
